import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useReceiveLogic } from 'src/hooks';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import animationData from 'src/images/lotties/cart-sending-bar.json';

type Props = {
  itemsCount: number;
  store?: string;
  onAfterRedirect?: () => void;
};

type Phase = 'S1_ONCE' | 'S1_LOOP' | 'S2_ONCE' | 'S2_LOOP' | 'S3_ONCE';

/**
 * Lottie frame segments used by the mini state machine below.
 * Keeping this at module scope ensures a stable reference across renders.
 */
const SEG = {
  S1_ONCE: [0, 41] as [number, number], // Initial "receiving cart id" pass
  S1_LOOP: [41, 52] as [number, number], // Short loop to simulate waiting during S1
  S2_ONCE: [52, 88] as [number, number], // Transition to "adding items"
  S2_LOOP: [88, 100] as [number, number], // Timed loop while adding items (with countdown)
  S3_ONCE: [100, 189] as [number, number], // Final redirect sequence
} as const;

/**
 * ReceiveChugAnimation
 * --------------------
 * Purpose:
 *   Drives a Lottie progress animation for the "receive" flow. Visually, it has:
 *     - Step 1: obtaining cart id (once + short loop)
 *     - Step 2: adding items (once + timed loop with countdown)
 *     - Step 3: final redirect sequence (once)
 *
 * Key ideas:
 *   - The animation is split into named segments (SEG.*).
 *   - We progress through phases via the Lottie `onComplete` callback (a small state machine).
 *   - We simulate "work time" using:
 *       * `step1LoopsTarget` — how many times the short loop in S1 should run, based on items count.
 *       * `step2Seconds`     — a countdown during S2_LOOP; can be longer for “slow stores”.
 *   - If the extension reports it’s “ready” (`readyRef.current === true`), we fast-forward
 *     from any current phase to the final segment (S3_ONCE).
 *   - If errors are present (`hasMissing` or `tooLong`), we skip all animation logic and show messages.
 */
export const ReceiveChugAnimation = ({ itemsCount, store }: Props) => {
  const [phase, setPhase] = useState<Phase>('S1_ONCE');
  // Number of completed S1_LOOP iterations (used to stop looping after N times).
  const [, setLoopsDone] = useState(0);
  // Countdown shown during S2_LOOP.
  const [secondsLeft, setSecondsLeft] = useState(0);

  const lottieRef = useRef<LottieRefCurrentProps>(null);
  // If extension signals "ready" we flip this to push ahead to S3.
  const fastForwardRef = useRef(false);

  const { t } = useTranslation();

  const {
    hasError,
    hasMissing,
    receiveUnsupported,
    readyRef,
    proceedToCartUrl,
  } = useReceiveLogic();

  let step1LoopsTarget = 0;
  let step2Seconds = 0;

  if (!hasError && itemsCount) {
    // Loop at least 3 times, at most 10, roughly itemsCount/3
    step1LoopsTarget = Math.max(3, Math.min(10, Math.ceil(itemsCount / 3)));
    // Some Amazon sub-stores are slower; grant more time per item.
    const isSlowStore =
      store?.endsWith('/fresh') || store?.endsWith('/localmarket');

    step2Seconds = isSlowStore ? itemsCount + 1 : 4;
  }

  /**
   * Small helper that plays a specific segment (inclusive range) on the Lottie instance.
   * We always stop first to ensure the segment starts at the exact frame.
   */
  const playSeg = useCallback((range: [number, number]) => {
    const inst = lottieRef.current;
    if (!inst) {
      return;
    }

    inst.stop();
    inst.playSegments([range[0], range[1]], true);
  }, []);

  /**
   * Effect #1: (Re)initialize animation when inputs change.
   * - Reset flags and counters.
   * - Set the seconds countdown for S2_LOOP.
   * - If items present, play S1_ONCE; otherwise position to its first frame.
   */
  useEffect(() => {
    if (hasError) {
      return;
    }

    fastForwardRef.current = false;
    setPhase('S1_ONCE');
    setLoopsDone(0);
    setSecondsLeft(step2Seconds);

    const inst = lottieRef.current;
    if (itemsCount > 0) {
      playSeg(SEG.S1_ONCE);
    } else if (inst) {
      inst.stop();
      inst.goToAndStop(SEG.S1_ONCE[0], true);
    }
  }, [hasError, itemsCount, playSeg, step2Seconds]);

  /**
   * Effect #2: When we are in S2_LOOP:
   * - Start a 1s interval to decrement the countdown.
   * - If `readyRef.current` is true, fast-forward to the final segment.
   * - Clear the interval on unmount/phase change.
   */
  useEffect(() => {
    if (hasError || phase !== 'S2_LOOP') {
      return;
    }

    const id = window.setInterval(() => {
      // Extension reported it's done – jump to S3 immediately.
      if (readyRef.current) {
        fastForwardRef.current = true;
        setPhase('S3_ONCE');
        playSeg(SEG.S3_ONCE);
        return;
      }
      // Normal countdown tick.
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next < 0) {
          window.clearInterval(id);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      window.clearInterval(id);
    };
  }, [phase, hasError, readyRef, playSeg]);

  // if there is an error, fast forward to the final segment
  useEffect(() => {
    if (hasError) {
      fastForwardRef.current = true;
      setPhase('S3_ONCE');
      playSeg(SEG.S3_ONCE);
    }
  }, [hasError, playSeg]);

  /**
   * onComplete: transitions between phases (mini state machine).
   * Runs each time the currently playing segment finishes.
   *
   * Flow:
   *   S1_ONCE -> (if need loops) S1_LOOP -> S2_ONCE -> S2_LOOP (countdown) -> S3_ONCE
   *   At any point, if `fastForwardRef.current` becomes true, push straight to S3_ONCE.
   */
  const onComplete = useCallback(() => {
    if (hasError) {
      return;
    }

    // If the extension is ready at any moment, plan to push straight to S3.
    if (readyRef.current) {
      fastForwardRef.current = true;
    }

    switch (phase) {
      case 'S1_ONCE': {
        if (fastForwardRef.current || step1LoopsTarget <= 0) {
          setPhase('S2_ONCE');
          playSeg(SEG.S2_ONCE);
        } else {
          setPhase('S1_LOOP');
          playSeg(SEG.S1_LOOP);
        }
        break;
      }

      case 'S1_LOOP': {
        if (fastForwardRef.current) {
          setPhase('S2_ONCE');
          playSeg(SEG.S2_ONCE);
          break;
        }
        setLoopsDone((n) => {
          const next = n + 1;
          if (next >= step1LoopsTarget) {
            setPhase('S2_ONCE');
            playSeg(SEG.S2_ONCE);
          } else {
            playSeg(SEG.S1_LOOP);
          }
          return next;
        });
        break;
      }

      case 'S2_ONCE': {
        if (fastForwardRef.current) {
          setPhase('S3_ONCE');
          playSeg(SEG.S3_ONCE);
        } else {
          setPhase('S2_LOOP');
          playSeg(SEG.S2_LOOP);
        }
        break;
      }

      case 'S2_LOOP': {
        if (fastForwardRef.current) {
          setPhase('S3_ONCE');
          playSeg(SEG.S3_ONCE);
          break;
        } else {
          playSeg(SEG.S2_LOOP);
        }
        break;
      }

      case 'S3_ONCE': {
        break;
      }
    }
  }, [hasError, phase, step1LoopsTarget, readyRef, playSeg]);

  return (
    <div className='z-0 -mt-8 flex w-full flex-col items-center justify-center gap-3 xs:-mt-12 sm:-mt-16'>
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        autoplay={false}
        loop={false}
        onComplete={onComplete}
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
      />

      {hasError ? (
        hasMissing ? (
          <>
            <p className='text-200 text-center'>
              <Trans
                i18nKey='receive.cart_loading_completed_with_errors'
                components={{
                  span: <span className='text-200 text-red-500' />,
                }}
              />
            </p>
            {proceedToCartUrl && (
              <a
                href={`/u/${encodeURIComponent(proceedToCartUrl)}`}
                rel='noopener noreferrer'
                className='button-primary h-12'
              >
                {t('receive.proceed_to_cart')}
                <OpenInNewIcon className='ml-2 text-offWhite' />
              </a>
            )}
          </>
        ) : receiveUnsupported ? (
          <div className='text-200 text-center text-red-500'>
            {t('popup.receive.retailer_requires_manual_cart_loading')}
          </div>
        ) : (
          <div className='text-200 text-center text-red-500'>
            {t('popup.receive.redirection_taking_too_long')}
          </div>
        )
      ) : (
        <div className='text-200 flex items-center justify-center gap-2'>
          <span className='text-200 text-pretty text-center'>
            {phase === 'S1_ONCE' || phase === 'S1_LOOP' ? (
              t('receive.receiving_cart_id')
            ) : phase === 'S2_ONCE' || phase === 'S2_LOOP' ? (
              t('receive.adding_items_to_cart')
            ) : (
              <span className='animate-pulse'>
                {t('receive.redirecting_to_cart')}
              </span>
            )}
          </span>
          {phase === 'S2_LOOP' && step2Seconds > 0 && (
            <>
              <span className='text-200'>•</span>
              {secondsLeft > 0 ? (
                <span className='text-200 text-pretty text-center text-gray-500'>
                  <Trans
                    i18nKey='receive.complete_in_x_seconds'
                    values={{ seconds: secondsLeft }}
                    components={{
                      span: (
                        <span
                          id='countdown-timer'
                          className='text-200 tabular-nums text-gray-500'
                        />
                      ),
                    }}
                  />
                </span>
              ) : (
                <span className='text-200 text-pretty text-center italic text-gray-500'>
                  {t('receive.almost_ready')}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
